import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  Code2,
  Smartphone,
  Layers,
  Users,
  Sparkles,
} from "lucide-react";

import FAQSection from "../components/FAQSection";
import { servicesFaqs } from "../data/faqs";
import BlogSection from "../components/BlogSection";
import { AnimatedCard } from "../components/AnimatedCard";
export const Services = () => {
  const services = [
    {
      icon: Code2,
      title: "Application Development Services",
      description:
        "Custom business applications designed to streamline operations, automate workflows, and support long-term growth.",
      path: "/application-development",
    },
    {
      icon: Layers,
      title: "Web Development Services",
      description:
        "Responsive, scalable, and high-performance web platforms built for modern customer and business experiences.",
      path: "/web-development",
    },
    {
      icon: Smartphone,
      title: "Mobile App Development",
      description:
        "Native and cross-platform mobile applications focused on usability, speed, and reliability.",
      path: "/mobile-app-development",
    },
    {
      icon: Sparkles,
      title: "AI & Machine Learning Development",
      description:
        "AI-powered applications, automation workflows, and intelligent models to improve decisions and productivity.",
      path: "/ai-ml-development",
    },
    {
      icon: Users,
      title: "Dedicated Developers / On-Demand Teams",
      description:
        "Extend your delivery capacity with dedicated engineers and on-demand teams aligned to your process.",
      path: "/hire-developers",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Software Development Services | Novaseeds Technologies</title>
        <meta
          name="description"
          content="Explore Novaseeds Technologies software development services including custom software, web apps, mobile apps, SaaS development, DevOps, and enterprise solutions."
        />

        {/* FAQ Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: servicesFaqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.answer
                }
              }))
            })
          }}
        />
      </Helmet>

      <div className="min-h-screen pt-32 pb-20 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">

          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-20"
          >
            <span className="text-sm uppercase tracking-widest text-muted-foreground font-bold">
              SERVICES
            </span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-none mt-4 mb-6">
              <span className="text-foreground">Comprehensive Tech Solutions</span><br/>
              <span className="text-gradient">For Every Business Need</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-3xl mx-auto">
              From concept to deployment and beyond, we offer end-to-end software
              development services to bring your vision to life.
            </p>
          </motion.div>

          {/* Services Grid */}
          <section className="services-cards-section mb-20 py-10 px-4 sm:px-6 lg:px-8 overflow-visible">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <AnimatedCard
                  key={service.path}
                  index={index}
                  slideFrom="alternate"
                  hoverScale={1.035}
                  hoverLift={-8}
                  className="group bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl hover:shadow-lg hover:border-blue-200/60"
                >
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:bg-blue-100/80">
                      <Icon size={28} className="text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground">{service.title}</h3>
                  </div>
                  <p className="text-muted-foreground mb-6">{service.description}</p>
                  <Link
                    to={service.path}
                    className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 hover:underline"
                  >
                    View Service
                  </Link>
                </AnimatedCard>
              );
            })}
          </div>
          </section>

          {/* CTA */}
          <AnimatedCard
            index={0}
            hoverScale={1.02}
            hoverLift={-6}
            className="bg-blue-50 border border-blue-200 rounded-3xl p-12 text-center mb-20"
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-6 text-foreground">
              Ready to Start Your Project?
            </h2>
            <p className="text-muted-foreground mb-8">
              Let's discuss how we can build the right technology solution for your business.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)]"
            >
              Get Free Consultation
            </Link>
          </AnimatedCard>
          {/* Blog Section */}
          <BlogSection categorySlug="software-development-company" title="Whats New In The Market " maxPosts={3} />
          {/* FAQ Section */}
          <FAQSection faqs={servicesFaqs} />

        </div>
      </div>
    </>
  );
};